import type { MapMouseEvent } from '@mapbox/mapbox-gl-draw';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { toast } from '@repo/design-system/components/ui/toaster';
import { useCreateMapRegionMutation, useDeleteMapRegionMutation, useUpdateMapRegionMutation } from '@repo/store/maps/api/mapsApi';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId } from '@repo/store/maps/slices/mapData';
import type { MapDataRegion, MapDataRegionGeometry } from '@repo/types/maps/map-types';
import { useCallback, useRef } from 'react';
import { useMapContext } from '../map-context';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import type MapboxGL from 'maplibre-gl';
export function useRegionOperations() {
  const {
    map
  } = useMapContext();
  const externalId = useAppSelector(selectExternalMapId);
  const drawRef = useRef<MapboxDraw | null>(null);
  const isDrawingRef = useRef(false);
  const editingRegionRef = useRef<MapDataRegion | null>(null);
  const [createMapRegion, {
    isLoading: isCreating
  }] = useCreateMapRegionMutation();
  const [updateMapRegion, {
    isLoading: isUpdating
  }] = useUpdateMapRegionMutation();
  const [deleteMapRegion, {
    isLoading: isDeleting
  }] = useDeleteMapRegionMutation();
  const handleDrawCreate = useCallback(async (e: {
    features: GeoJSON.Feature[];
  }) => {
    if (!externalId || !isDrawingRef.current) return;
    const feature = e.features[0];
    if (!feature || !feature.geometry) return;

    // Only accept Polygon or MultiPolygon geometries for regions
    if (feature.geometry.type !== 'Polygon' && feature.geometry.type !== 'MultiPolygon') {
      toast({
        title: 'Invalid geometry',
        description: 'Regions must be polygons',
        variant: 'error'
      });
      return;
    }
    try {
      const region = await createMapRegion({
        gameMapExternalId: externalId,
        geometry: feature.geometry as unknown as MapDataRegionGeometry,
        properties: {
          title: 'New Region',
          color: '#3b82f6',
          lineThickness: 2,
          minZoom: 1,
          maxZoom: 20
        }
      }).unwrap();

      // Remove the temporary draw feature and add the actual region
      if (drawRef.current && feature.id) {
        drawRef.current.delete(feature.id as string);
      }
      toast({
        title: 'Region created',
        description: 'You can now edit its properties',
        variant: 'success'
      });
      isDrawingRef.current = false;

      // Clean up draw control after creating
      if (drawRef.current && map) {
        drawRef.current.changeMode('simple_select');
        drawRef.current.deleteAll();
        try {
          map.removeControl(drawRef.current as unknown as MapboxGL.IControl);
          drawRef.current = null;
        } catch (e_0) {
          console.warn('Failed to remove draw control:', e_0);
        }
      }
      return region;
    } catch (error) {
      toast({
        title: 'Failed to create region',
        description: JSON.stringify(error),
        variant: 'error'
      });
      // Remove the failed feature
      if (drawRef.current && feature.id) {
        drawRef.current.delete(feature.id as string);
      }
      isDrawingRef.current = false;
    }
  }, [externalId, createMapRegion, map]);
  const handleDrawUpdate = useCallback(async (e_1: {
    features: GeoJSON.Feature[];
  }) => {
    // Handle region geometry updates
    if (!externalId || !editingRegionRef.current) return;
    const feature_0 = e_1.features[0];
    if (!feature_0 || !feature_0.geometry) return;

    // Only accept Polygon or MultiPolygon geometries for regions
    if (feature_0.geometry.type !== 'Polygon' && feature_0.geometry.type !== 'MultiPolygon') {
      toast({
        title: 'Invalid geometry',
        description: 'Regions must be polygons',
        variant: 'error'
      });
      return;
    }
    const regionId = editingRegionRef.current.properties.externalId;
    const currentProperties = editingRegionRef.current.properties;
    try {
      await updateMapRegion({
        externalId: regionId,
        request: {
          properties: {
            title: currentProperties.title,
            color: currentProperties.color,
            lineThickness: currentProperties.lineThickness,
            minZoom: currentProperties.minZoom,
            maxZoom: currentProperties.maxZoom
          },
          geometry: feature_0.geometry as unknown as MapDataRegionGeometry
        },
        gameMapExternalId: externalId
      }).unwrap();
      toast({
        title: 'Region shape updated',
        variant: 'success'
      });
    } catch (error_0) {
      toast({
        title: 'Failed to update region shape',
        description: JSON.stringify(error_0),
        variant: 'error'
      });
    }
  }, [externalId, updateMapRegion]);
  const handleDrawDelete = useCallback(async () => {
    // Handle region deletion if needed
  }, []);
  const stopDrawingRegion = useCallback(() => {
    if (!drawRef.current || !map) return;
    isDrawingRef.current = false;
    drawRef.current.changeMode('simple_select');
    drawRef.current.deleteAll();

    // Remove the control from the map
    try {
      map.removeControl(drawRef.current as unknown as MapboxGL.IControl);
      drawRef.current = null;
    } catch (e_2) {
      console.warn('Failed to remove draw control:', e_2);
    }
  }, [map]);
  const updateRegionProperties = useCallback(async (regionId_0: string, properties: {
    title?: string;
    color?: string;
    lineThickness?: number;
    minZoom?: number;
    maxZoom?: number;
  }, geometry: MapDataRegion['geometry']) => {
    if (!externalId) return;
    try {
      // Ensure all required fields are present
      const completeProperties = {
        title: properties.title ?? 'Unnamed Region',
        color: properties.color ?? '#3b82f6',
        lineThickness: properties.lineThickness ?? 2,
        minZoom: properties.minZoom ?? 1,
        maxZoom: properties.maxZoom ?? 20
      };
      const region_0 = await updateMapRegion({
        externalId: regionId_0,
        request: {
          properties: completeProperties,
          geometry
        },
        gameMapExternalId: externalId
      }).unwrap();
      toast({
        title: 'Region updated',
        variant: 'success'
      });
      return region_0;
    } catch (error_1) {
      toast({
        title: 'Failed to update region',
        description: JSON.stringify(error_1),
        variant: 'error'
      });
    }
  }, [externalId, updateMapRegion]);
  const deleteRegionById = useCallback(async (regionId_1: string) => {
    if (!externalId) return;
    try {
      await deleteMapRegion({
        externalId: regionId_1,
        gameMapExternalId: externalId
      }).unwrap();
      toast({
        title: 'Region deleted',
        variant: 'success'
      });
    } catch (error_2) {
      toast({
        title: 'Failed to delete region',
        description: JSON.stringify(error_2),
        variant: 'error'
      });
    }
  }, [externalId, deleteMapRegion]);
  const stopEditingGeometry = useCallback(() => {
    if (!drawRef.current || !map) return;
    editingRegionRef.current = null;
    drawRef.current.changeMode('simple_select');
    drawRef.current.deleteAll();

    // Remove the control from the map
    try {
      map.removeControl(drawRef.current as unknown as MapboxGL.IControl);
      drawRef.current = null;
    } catch (e_3) {
      console.warn('Failed to remove draw control:', e_3);
    }
  }, [map]);

  // Initialize MapboxDraw lazily
  const initializeDraw = useCallback(() => {
    if (!map || drawRef.current) return drawRef.current;
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: false,
        trash: false
      },
      defaultMode: 'simple_select',
      styles: [{
        id: 'gl-draw-polygon-fill',
        type: 'fill',
        filter: ['all', ['==', '$type', 'Polygon']],
        paint: {
          'fill-color': '#3b82f6',
          'fill-opacity': 0.3
        }
      }, {
        id: 'gl-draw-polygon-stroke',
        type: 'line',
        filter: ['all', ['==', '$type', 'Polygon']],
        paint: {
          'line-color': '#3b82f6',
          'line-width': 2
        }
      }, {
        id: 'gl-draw-vertex',
        type: 'circle',
        filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
        paint: {
          'circle-radius': 5,
          'circle-color': '#fff',
          'circle-stroke-color': '#3b82f6',
          'circle-stroke-width': 2
        }
      }, {
        id: 'gl-draw-midpoint',
        type: 'circle',
        filter: ['all', ['==', 'meta', 'midpoint'], ['==', '$type', 'Point']],
        paint: {
          'circle-radius': 3,
          'circle-color': '#3b82f6'
        }
      }]
    });
    map.addControl(draw as unknown as MapboxGL.IControl, 'top-right');
    drawRef.current = draw;

    // Listen for draw events
    map.on('draw.create', handleDrawCreate);
    map.on('draw.update', handleDrawUpdate);
    map.on('draw.delete', handleDrawDelete);

    // Add Alt+click handler for vertex deletion
    const handleMapClick = (e_4: MapMouseEvent) => {
      if (!e_4.originalEvent.altKey || !drawRef.current) return;
      const selectedFeatures = drawRef.current.getSelected();
      if (selectedFeatures.features.length === 0) return;
      const feature_1 = selectedFeatures.features[0];
      if (feature_1?.geometry?.type !== 'Polygon') return;
      const clickPoint: [number, number] = [e_4.lngLat.lng, e_4.lngLat.lat];
      const coordinates = feature_1.geometry.coordinates[0];
      let closestIndex = -1;
      let minDistance = Infinity;
      coordinates?.forEach((coord: number[], index: number) => {
        if (index === coordinates.length - 1) return;
        const vertexPoint = map.project([coord?.[0] ?? 0, coord?.[1] ?? 0] as [number, number]);
        const clickPixel = map.project(clickPoint);
        const distance = Math.sqrt((vertexPoint.x - clickPixel.x) ** 2 + (vertexPoint.y - clickPixel.y) ** 2);
        if (distance < minDistance && distance < 10) {
          minDistance = distance;
          closestIndex = index;
        }
      });
      if (closestIndex !== -1 && coordinates && coordinates.length > 4) {
        const newCoordinates = [...coordinates];
        newCoordinates.splice(closestIndex, 1);
        if (newCoordinates.length > 0 && newCoordinates[0]) {
          newCoordinates[newCoordinates.length - 1] = [...newCoordinates[0]];
        }
        feature_1.geometry.coordinates = [newCoordinates];
        drawRef.current.add(feature_1);
        drawRef.current.changeMode('direct_select', {
          featureId: feature_1.id as string
        });
        toast({
          title: 'Vertex removed',
          description: 'Alt+click to remove more vertices',
          variant: 'info'
        });
        void handleDrawUpdate({
          features: [feature_1]
        });
      }
    };
    map.on('click', handleMapClick);
    return draw;
  }, [map, handleDrawCreate, handleDrawDelete, handleDrawUpdate]);
  const startDrawingRegion = useCallback(() => {
    // Clear any existing editing state
    editingRegionRef.current = null;
    const draw_0 = initializeDraw();
    if (!draw_0) {
      toast({
        title: 'Drawing tools not initialized',
        description: 'Please wait for the map to fully load',
        variant: 'error'
      });
      return;
    }

    // Clear any existing features first
    draw_0.deleteAll();
    isDrawingRef.current = true;
    draw_0.changeMode('draw_polygon');
    toast({
      title: 'Drawing mode',
      description: 'Click to add points, double-click to finish',
      variant: 'info'
    });
  }, [initializeDraw]);
  const editRegionGeometry = useCallback((region_1: MapDataRegion) => {
    const draw_1 = initializeDraw();
    if (!draw_1) return;
    editingRegionRef.current = region_1;
    draw_1.deleteAll();
    const feature_2: GeoJSON.Feature = {
      type: 'Feature',
      id: region_1.properties.externalId,
      geometry: region_1.geometry as unknown as GeoJSON.Geometry,
      properties: region_1.properties
    };
    draw_1.add(feature_2);
    draw_1.changeMode('direct_select', {
      featureId: region_1.properties.externalId
    });
    toast({
      title: 'Editing region',
      description: 'Drag vertices to move, click midpoints to add, Alt+click vertices to delete',
      variant: 'info'
    });
  }, [initializeDraw]);
  return {
    startDrawingRegion,
    stopDrawingRegion,
    updateRegionProperties,
    deleteRegionById,
    editRegionGeometry,
    stopEditingGeometry,
    isCreating,
    isUpdating,
    isDeleting,
    draw: drawRef.current
  };
}